import { frappeList, frappeGet } from "./utils";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { docTypeListWithPagination } from "./docs";

/**
 * Default fields to fetch for Sales Invoice list views
 *
 * This constant ensures consistency across:
 * - salesInvoices.list() (legacy, if needed)
 * - salesInvoices.listWithPagination() defaultFields
 * - useSalesInvoiceListWithPagination fields
 * - createSalesInvoiceColumns accessorKeys
 *
 * IMPORTANT:
 * - If you add a new column to createSalesInvoiceColumns, add the corresponding field here.
 * - Note: "modified" can be used for sorting even if not included here. If you add a
 *   "Last Updated" column later, add "modified" to this array.
 */
export const SALES_INVOICE_LIST_FIELDS = [
  "name",
  "customer",
  "posting_date",
  "status",
  "grand_total",
  "outstanding_amount",
  "company",
] as const;

/**
 * Field configuration for Sales Invoice list settings
 * Includes "name" since it's rendered as the visible "Invoice #" column
 */
export const SALES_INVOICE_LIST_FIELD_CONFIG = [
  { fieldname: "name", label: "Invoice #", required: true },
  { fieldname: "customer", label: "Customer", required: true },
  { fieldname: "posting_date", label: "Posting Date" },
  { fieldname: "status", label: "Status" },
  { fieldname: "grand_total", label: "Grand Total" },
  { fieldname: "outstanding_amount", label: "Outstanding" },
  { fieldname: "company", label: "Company" },
] as const;

export interface SalesInvoiceListParams {
  /**
   * Pre-mapped Frappe filter array, e.g.:
   * [["Sales Invoice", "status", "=", "Paid"]]
   */
  filters?: FrappeFilterArray;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export type SalesInvoice = {
  name: string;
  customer: string;
  posting_date: string;
  status: string;
  grand_total: number;
  outstanding_amount: number;
  company: string;
  // Allow additional backend fields without breaking typing
  [key: string]: any;
};

export const salesInvoices = {
  /**
   * List all sales invoices (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<SalesInvoice[]> {
    return frappeList<SalesInvoice>(
      "Sales Invoice",
      filters,
      [...SALES_INVOICE_LIST_FIELDS]
    );
  },

  /**
   * List sales invoices with server-side pagination and filtering.
   *
   * This currently uses the generic docTypeListWithPagination path without
   * any special logic. If you later need Sales Invoice–specific backend
   * behavior, you can add it here or use docTypeListWithPaginationViaMethod.
   */
  listWithPagination(
    params: SalesInvoiceListParams
  ): Promise<FrappeListResponse<SalesInvoice>> {
    const {
      filters = [],
      pageIndex = 0,
      pageSize = 20,
      orderBy,
      fields,
    } = params;

    const defaultFields = fields ?? [...SALES_INVOICE_LIST_FIELDS];

    return docTypeListWithPagination<SalesInvoice>({
      doctype: "Sales Invoice",
      filters,
      pageIndex,
      pageSize,
      orderBy: orderBy ?? "posting_date desc",
      fields: defaultFields,
    });
  },

  /**
   * Get a single sales invoice by name
   */
  detail(name: string): Promise<SalesInvoice> {
    return frappeGet<SalesInvoice>("Sales Invoice", name);
  },
};

